import React, {useEffect, useState} from "react";
import type {Producer, ProducerMode} from "@/ReactInit/Scripts/DashboardPage/types";
import {DashboardCard} from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import {DashboardButton, DashboardIconButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import DashboardInputGroup from "@/ReactInit/Scripts/DashboardPage/components/DashboardInputGroup";
import {DashboardAlertBox} from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import {saveProducer, setPhoneActivation} from "@/ReactInit/Scripts/DashboardPage/api/dashboardApi";
import {
  digitsOnly,
  formatNorwegianPhone,
  formatOrgnrInput,
  isValidEmail,
  isValidNorwegianPhone,
  isValidOrgnr,
  norwegianNationalPhone, toNorwegianE164,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import {normalizeId} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import {useIsMobile} from "@/ReactInit/Scripts/DashboardPage/hooks/useBreakpoint";
import {scrollToId} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardScroll";
import {withMinDuration} from "@/ReactInit/Scripts/DashboardPage/utils/minDuration";

type NewUserRow = {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  phoneId?: string | null;
};

interface AdminDashboardProducerFormViewProps {
  mode: ProducerMode;
  producer: Producer | null;
  onCancel: () => void;
  onSaved: (producerId: string | null) => void
}

const mkRow = (patch?: Partial<NewUserRow>): NewUserRow => ({
  id: patch?.id ?? `row-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  name: patch?.name ?? "",
  phoneNumber: patch?.phoneNumber ?? "",
  email: patch?.email ?? "",
  phoneId: patch?.phoneId ?? null,
});

const DashboardProducerFormView = (
  {
    mode,
    producer,
    onCancel,
    onSaved,
  }: AdminDashboardProducerFormViewProps) => {
  const isEdit = mode === "edit";
  const isMobile = useIsMobile();

  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [producerForm, setProducerForm] = useState<{ producerName: string; orgnr: string }>({
    producerName: "",
    orgnr: "",
  });

  const validateProducerName = (val: string) => {
    if (!val.trim()) return "Virksomhetsnavn er påkrevd.";
    return null;
  };

  const validateOrgnr = (val: string) => {
    if (!val.trim()) return "Organisasjonsnummer er påkrevd.";
    return isValidOrgnr(val) ? null : "Organisasjonsnummer må bestå av 9 siffer.";
  };

  const validatePhone = (val: string) => {
    if (!val.trim()) return "Telefonnummer er påkrevd.";
    return isValidNorwegianPhone(val) ? null : "Telefonnummer må ha 8 siffer.";
  };

  const validateEmail = (val: string) => {
    if (!val.trim()) return "E-post er påkrevd.";
    return isValidEmail(val) ? null : "Skriv inn en gyldig e-postadresse.";
  };

  const getFirstErrorId = (): string | null => {
    if (validateProducerName(producerForm.producerName)) return "producer-name";
    if (validateOrgnr(producerForm.orgnr)) return "producer-orgnr";

    for (const row of userRows) {
      if (validatePhone(row.phoneNumber)) return `user-phone-${row.id}`;
      if (validateEmail(row.email)) return `user-email-${row.id}`;
    }

    return null;
  };


  const norwegianFlag = "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgdmlld0JveD0iMCAwIDIyIDE2Ij4KCTx0aXRsZT5GbGFnIG9mIE5vcndheTwvdGl0bGU+Cgk8cmVjdCB3aWR0aD0iMjIiIGhlaWdodD0iMTYiIGZpbGw9IiNiYTBjMmYiLz4KCTxwYXRoIGQ9Ik0wLDhoMjJNOCwwdjE2IiBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iNCIvPgoJPHBhdGggZD0iTTAsOGgyMk04LDB2MTYiIHN0cm9rZT0iIzAwMjA1YiIgc3Ryb2tlLXdpZHRoPSIyIi8+Cjwvc3ZnPg==";

  const [userRows, setUserRows] = useState<NewUserRow[]>([mkRow()]);

  const [phoneIdsMarkedForDeactivation, setPhoneIdsMarkedForDeactivation] = useState<Set<string>>(new Set());


  useEffect(() => {
    setSubmitAttempted(false);
    setFormError(null);
    setPhoneIdsMarkedForDeactivation(new Set());

    if (isEdit) {
      if (!producer) {
        setProducerForm({producerName: "", orgnr: ""});
        setUserRows([mkRow()]);
        return;
      }

      setProducerForm({
        producerName: producer.name ?? "",
        orgnr: formatOrgnrInput(producer.orgnr ?? ""),
      });

      const activePhones = (producer.phoneNumbers ?? []).filter((ph) => !ph.deactivated);

      const rowsFromProducer =
        activePhones.length > 0
          ? activePhones.map((ph) =>
            mkRow({
              id: ph.id?.trim() ? ph.id : `row-${producer.id}-${ph.phoneNumber ?? ""}`,
              phoneId: normalizeId(ph.id),
              name: ph.name ?? "",
              phoneNumber: formatNorwegianPhone(norwegianNationalPhone(ph.phoneNumber ?? "")),
              email: ph.email ?? "",
            }),
          )
          : [mkRow({email: ""})];

      setUserRows(rowsFromProducer);
    } else {
      setProducerForm({producerName: "", orgnr: ""});
      setUserRows([mkRow()]);
    }
  }, [mode, producer?.id]);

  const addUserRow = () => {
    setUserRows((prev) => [...prev, mkRow()]);
  };

  const updateUserRow = (rowId: string, patch: Partial<NewUserRow>) => {
    setUserRows((prev) => prev.map((r) => (r.id === rowId ? {...r, ...patch} : r)));
  };

  const removeUserRow = (rowId: string) => {
    setUserRows((prev) => {
      if (prev.length <= 1) return prev;

      const row = prev.find((r) => r.id === rowId);
      const phoneId = normalizeId(row?.phoneId);

      if (isEdit && phoneId) {
        setPhoneIdsMarkedForDeactivation((curr) => {
          const next = new Set(curr);
          next.add(phoneId);
          return next;
        });
      }

      return prev.filter((r) => r.id !== rowId);
    });
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);

    const firstErrorId = getFirstErrorId();
    if (firstErrorId) {
      setFormError("Rett opp feltene markert i rødt før du lagrer.");
      scrollToId("producer-form-error", {
        durationMs: 450,
        easing: "easeInOutCubic",
        offsetTop: 15,
        retryNextFrame: true,
        onlyIfNotInView: true,
        inViewThresholdPx: 12,
      });
      return;
    }

    const payload = {
      producerId: isEdit ? producer?.id ?? null : null,
      orgnr: digitsOnly(producerForm.orgnr),
      producerName: producerForm.producerName.trim(),
      address: null,
      phoneNumbers: userRows.map((r) => ({
        phoneId: isEdit ? normalizeId(r.phoneId) : null,
        producerId: isEdit ? producer?.id ?? null : null,
        phoneNumber: toNorwegianE164(r.phoneNumber),
        name: r.name.trim(),
        email: r.email.trim(),
      })),
    };

    try {
      setFormError(null);
      setIsSaving(true);

      await withMinDuration(async () => {
        await saveProducer(payload);

        if (isEdit && producer?.id) {
          const ids = Array.from(phoneIdsMarkedForDeactivation);
          if (ids.length > 0) {
            await Promise.all(
              ids.map((phoneId) =>
                setPhoneActivation({
                  producerId: producer.id,
                  phoneId,
                  deactivated: true,
                }),
              ),
            );
          }
        }
      });

      onSaved(isEdit ? producer?.id ?? null : null);
    } catch (e) {
      console.error(e);
      setFormError(e instanceof Error ? e.message : "Noe gikk galt ved lagring av virksomhet.");
      scrollToId("producer-form-error", {durationMs: 350, offsetTop: 15, retryNextFrame: true});
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 tablet:gap-10 w-full mx-auto max-w-[932px]">
      <DashboardCard>
        <div className="flex flex-col gap-5 tablet:gap-10">
          {formError && <div id="producer-form-error"><DashboardAlertBox type="error" label={formError}/></div>}
          <DashboardInputGroup
            inputs={[
              {
                id: "producer-name",
                label: "Virksomhetsnavn",
                value: producerForm.producerName,
                placeholder: "Navn på virksomhet",
                onChange: (value) => setProducerForm((p) => ({...p, producerName: value})),
                required: true,
                validate: validateProducerName,
                forceTouched: submitAttempted,
              },
              {
                id: "producer-orgnr",
                label: "Organisasjonsnummer",
                value: producerForm.orgnr,
                placeholder: "123 456 789",
                onChange: (value) => setProducerForm((p) => ({...p, orgnr: formatOrgnrInput(value)})),
                required: true,
                validate: validateOrgnr,
                forceTouched: submitAttempted,
              },
            ]}
          />
          <hr className="border-t border-black-muted"/>
          <div className="flex flex-col gap-4">
            <h3 className="text-3xl">Kontaktperson</h3>
            <div className="flex flex-col gap-4">
              {userRows.map((row, idx) => {
                  const firstId = idx === 0;
                  return (
                    <div key={row.id} className="flex flex-col tablet:flex-row items-center gap-5">
                      {isMobile && idx > 0 && <hr className="w-full my-2 border-t border-black-muted"/>}
                      <DashboardInputGroup
                        grids="3"
                        inputs={[
                          {
                            id: `user-name-${row.id}`,
                            label: "Navn",
                            value: row.name,
                            placeholder: "Navn på person",
                            onChange: (value) => updateUserRow(row.id, {name: value}),
                            required: false,
                          },
                          {
                            id: `user-phone-${row.id}`,
                            label: "Telefonnummer",
                            value: row.phoneNumber,
                            placeholder: "999 88 777",
                            onChange: (value) =>
                              updateUserRow(row.id, {phoneNumber: formatNorwegianPhone(value)}),
                            required: true,
                            inputMode: "numeric",
                            prefix: <img alt="Norge" src={norwegianFlag} aria-hidden="true"
                                         className="h-6 w-6 object-contain rounded-lg"/>,
                            validate: (val) => {
                              if (!val.trim()) return "Telefonnummer er påkrevd.";
                              return isValidNorwegianPhone(val) ? null : "Telefonnummer må ha 8 siffer.";
                            },
                            forceTouched: submitAttempted,
                          },
                          {
                            id: `user-email-${row.id}`,
                            label: "E-post",
                            value: row.email,
                            placeholder: "ola@nordmann.no",
                            onChange: (value) => updateUserRow(row.id, {email: value}),
                            required: true,
                            type: "email",
                            autoComplete: "email",
                            validate: (val) => {
                              if (!val.trim()) return "E-post er påkrevd.";
                              return isValidEmail(val) ? null : "Skriv inn en gyldig e-postadresse.";
                            },
                            forceTouched: submitAttempted,
                          }
                        ]}
                      />
                      {isMobile ? (
                        <DashboardButton
                          label="Fjern bruker"
                          variant="sidebar"
                          rounded="full"
                          icon="icon-x"
                          onClick={() => {
                            if (firstId) return;
                            removeUserRow(row.id);
                          }}
                          className={firstId ? "hidden" : ""}
                          disabled={isSaving || firstId}/>
                      ) : (
                        <DashboardIconButton
                          label={`Fjern bruker ${idx + 1}`}
                          variant="sidebar"
                          rounded="full"
                          icon="icon-x"
                          size="3"
                          onClick={() => {
                            if (firstId) return;
                            removeUserRow(row.id);
                          }}
                          disabled={isSaving || firstId}
                        />)}
                    </div>
                  )
                }
              )}
            </div>
          </div>
          <DashboardButton
            label="Legg til flere brukere"
            variant="underline"
            onClick={addUserRow}
            disabled={isSaving}
            icon="icon-plus"
          />
        </div>
      </DashboardCard>
      <div className="flex flex-row justify-center gap-3 mt-2">
        <DashboardButton
          onClick={onCancel}
          label="Avbryt"
          variant="underline"
          disabled={isSaving}
          icon="icon-x"
        />
        <DashboardButton
          onClick={handleSubmit}
          label={isEdit ? "Lagre endringer" : "Fullfør"}
          variant="darkplain"
          loading={isSaving}
          loadingText="Lagrer…"
          disabled={isSaving}
          icon="icon-check"
        />
      </div>
    </div>
  );
};

export default DashboardProducerFormView;
